// Utility: toDisplayText.js
// Purpose: Coerce ANYTHING into a safe, human-readable string for a React child.
//
//! D3 (live-found 2026-07-13) — AN ERROR MESSAGE MUST NEVER CRASH THE SCREEN.
//!  The operator placed a real live order. It was fail-closed with a 422 (`bis_unavailable` — the
//!  BIS owner-resolve timed out on a cold connection pool). Instead of being told WHY, they got:
//!
//!      "SOMETHING WENT WRONG IN SMARTTRADE
//!       Error: Objects are not valid as a React child (found: object ...)"
//!
//!  The entire SmartTrade subtree fell to the error boundary and the ACTUAL reason was never shown.
//!  So at the exact moment the operator most needed a message — a LIVE ORDER had just been rejected
//!  and they needed to know whether money was at risk — the message itself took down the screen.
//!
//!  An unreadable error is bad. A CRASH that HIDES the error is far worse: it destroys the
//!  information the operator needs in order to act.
//!
//!  Backend errors arrive in several shapes and we cannot enumerate them all:
//!    - a plain string                      -> "Insufficient limit"
//!    - our envelope                        -> { code, message }
//!    - a FastAPI 422 validation error      -> [ { type, loc, msg, input }, ... ]   (an ARRAY!)
//!    - an axios error                      -> { response: { data: { detail } } }
//!    - something new nobody predicted
//!  Rather than chase each shape at each call site, coerce at the RENDER BOUNDARY. This is the
//!  last line: whatever arrives is rendered as TEXT, never as a React child.

/**
 * toDisplayText — turn any value into a string safe to render as a React child.
 *
 * Never throws. Never returns an object/array. Prefers the most human-readable field available.
 *
 * @param {*} value            anything: string, Error, {code,message}, FastAPI detail array, ...
 * @param {string} [fallback]  returned when nothing readable can be extracted
 * @returns {string}
 */
export function toDisplayText(value, fallback = 'An unexpected error occurred') {
  if (value === null || value === undefined) return fallback;

  // --> Already safe.
  if (typeof value === 'string') return value.trim() || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  // --> Error instances (and anything Error-like).
  if (value instanceof Error) return value.message || fallback;

  // --> FastAPI 422: `detail` is an ARRAY of {type, loc, msg, input}. Note `typeof [] === 'object'`,
  //     which is precisely how an array sneaks past a naive `typeof x === 'object'` guard and
  //     reaches React as a child.
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => toDisplayText(v, ''))
      .filter(Boolean);
    return parts.length ? parts.join('; ') : fallback;
  }

  if (typeof value === 'object') {
    //--> Our standard error envelope + the common backend shapes, most human-readable first.
    //*   `msg` is FastAPI's per-item validation key; `detail` nests our own envelope.
    const candidate =
      value.message ??
      value.msg ??
      value.detail ??
      value.error ??
      value.reason ??
      value.code;

    if (candidate !== undefined && candidate !== null && candidate !== value) {
      return toDisplayText(candidate, fallback);
    }

    //--> Nothing readable — show the raw shape rather than crashing. An ugly string still tells the
    //*   operator SOMETHING; a crash tells them nothing and hides the failure entirely.
    try {
      const json = JSON.stringify(value);
      return json && json !== '{}' ? json : fallback;
    } catch {
      return fallback; // circular refs etc.
    }
  }

  return fallback;
}

/**
 * formatNodeValue — the ONE scalar formatter for live node values (moved from
 * the former SignalEvaluationPanel local formatter; indicator-docs-panel R4).
 * Dash for null/undefined/non-finite; booleans & integers unchanged; finite non-integers to 4
 * decimals with trailing zeros trimmed; strings preserved; arrays/objects summarised compactly.
 */
export function formatNodeValue(v) {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '—';
    if (Number.isInteger(v)) return String(v);
    //--> four fractional digits, trailing zeros trimmed by Number(); a value that rounds
    //    to 0 (e.g. 0.00004) is shown as '<0.0001' so a live non-zero never reads as zero
    const fixed = Number(v.toFixed(4));
    return fixed === 0 ? (v > 0 ? '<0.0001' : '>-0.0001') : String(fixed);
  }
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return `[${v.map((x) => formatNodeValue(x)).join(', ')}]`;
  if (typeof v === 'object') return '{...}';
  return String(v);
}

export default toDisplayText;
